import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import HeaderOrg from "../Headers/HeaderOrg";
import ManageTitle from "../../Components/TitleElements/ManageTitle";
import Loader from "../../Components/Loader/Loader";
import Help from "../../Components/Help/Help";
import { ShowToastMessage } from "../../helper/ShowToastMessage";
import { serviceConsumer } from "../../network/ServiceConsumer";
import {
  getDrawDashboard,
  getDraw,
  getDrawsExport,
  downloadEventDayPdf,
} from "../../network/oireachtasEventDay";
import { exportDrawsToExcel } from "./exportDrawsToExcel";
import { useJobDevice } from "../../device/DeviceContext";

// Competitions — every competition with its entrant count and draw status,
// Excel export of everything. Drill-down (?comp=): the running order and,
// once locked, the event-day PDFs + a single-comp export. Drawing and locking
// moved to the Draw screen (Draw/OireachtasDrawRoom.jsx, card d76b3080; Devin
// 2026-10-19: remove the old draw), so this screen no longer draws.

const OireachtasDraw = () => {
  const [event, setEvent] = useState(null);
  const [rows, setRows] = useState([]);
  const [draw, setDraw] = useState(null);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // On an event-day device (card 540f21a4) the list is read only: no Draw
  // screen, no export of every draw; prints as the job includes.
  const { device: jobDevice, can } = useJobDevice();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const eventId = params.get("id");
  const selected = params.get("comp") || "";

  const loadDashboard = async () => {
    const res = await getDrawDashboard(eventId);
    setRows(res.rows || []);
    return res.rows || [];
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [eventRes] = await Promise.all([
          serviceConsumer("GET", `${process.env.REACT_APP_BASE_URL}/oireachtas/${eventId}`),
          loadDashboard(),
        ]);
        setEvent(eventRes.event);
      } catch (e) {
        ShowToastMessage("error", e?.messages || "Could not load competitions");
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId]);

  // Drill-down draw loads off the ?comp= param so back/forward and reloads work.
  useEffect(() => {
    setDraw(null);
    if (!selected) return;
    (async () => {
      try {
        setBusy(true);
        const res = await getDraw(eventId, selected);
        setDraw(res.draw);
      } catch (e) {
        ShowToastMessage("error", e?.messages || "Could not load draw");
      } finally {
        setBusy(false);
      }
    })();
  }, [eventId, selected]);

  const openComp = (compId) => navigate(`/oireachtas/draw?id=${eventId}&comp=${compId}`);
  const backToList = () => navigate(`/oireachtas/draw?id=${eventId}`);

  // ---- actions -----------------------------------------------------------------

  const exportName = (suffix) =>
    `${(event?.name || "oireachtas").replace(/[^\w-]+/g, "-")}-${suffix}.xlsx`;

  const doExportAll = async () => {
    try {
      setBusy(true);
      const res = await getDrawsExport(eventId);
      await exportDrawsToExcel(res.rows || [], exportName("draws"));
    } catch (e) {
      ShowToastMessage("error", e?.messages || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const dl = async (path, suffix) => {
    try {
      setBusy(true);
      const code = selectedRow()?.competition?.code || "comp";
      await downloadEventDayPdf(eventId, selected, path, `${code}-${suffix}.pdf`);
    } catch (e) {
      ShowToastMessage("error", "The PDF could not be opened");
    } finally {
      setBusy(false);
    }
  };

  const doExportComp = async () => {
    const row = selectedRow();
    if (!row || !draw) return;
    try {
      setBusy(true);
      await exportDrawsToExcel(
        [{ competition: row.competition, entrantCount: row.entrantCount, draw }],
        exportName(row.competition.code)
      );
    } catch (e) {
      ShowToastMessage("error", e?.messages || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const selectedRow = () => rows.find((r) => String(r.competition._id) === String(selected));
  const eventDate = (event?.eventDays && event.eventDays[0]) || event?.entriesCloseDate;
  const locked = !!draw?.locked;

  const statusText = (r) => {
    if (!r.draw) return "no draw";
    if (r.draw.locked) return `locked v${r.draw.version}`;
    return `draft v${r.draw.version}`;
  };

  const filtered = rows.filter((r) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      String(r.competition.code).toLowerCase().includes(q) ||
      String(r.competition.name || "").toLowerCase().includes(q)
    );
  });

  return (
    <>
      {loading && <Loader />}
      <HeaderOrg />
      <ManageTitle
        eventLogo={event?.logo}
        eventName={event?.name}
        eventType={event?.eventType}
        eventDate={eventDate}
        loading={loading}
        textButton={!selected && !jobDevice ? "Export all (Excel)" : undefined}
        className={busy || loading ? "text-btn-disabled" : "text-btn"}
        textBtnHandler={!selected && !jobDevice ? doExportAll : undefined}
        onBackClick={() =>
          setTimeout(
            () => (selected ? backToList() : navigate(`/oireachtas/manage?id=${eventId}`)),
            200
          )
        }
      />
      {!loading && !selected && (
        <div className="db-body">
          {/* Drawing moved to the Draw screen (Devin 2026-10-19: remove the old
              draw); this screen keeps the lists, prints and exports. */}
          {!jobDevice && (
          <div className="grey-42 mt-3" style={{ fontSize: "13px" }}>
            Draws are made and locked on the{" "}
            <button className="text-btn" onClick={() => navigate(`/oireachtas/draws?id=${eventId}`)}>
              Draw screen
            </button>
            . This screen lists each competition&apos;s entrants and, once locked, its order, prints and exports.
          </div>
          )}

          <div className="mt-4">
            <div className="search-label">Search:</div>
            <input
              type="text"
              className="searchFeisComp"
              placeholder="Code or name"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>

          <div style={{ width: "100%", overflowX: "auto", marginTop: "30px", marginBottom: "50px" }}>
            <table className="table my_table competitorTable ">
              <tbody>
                <tr className="header-row">
                  <td className="w-10 p-25-5">Code</td>
                  <td className="w-35 p-25-5">Competition</td>
                  <td className="text-center w-10 p-25-5">Entrants</td>
                  <td className="w-10 p-25-5">Status</td>
                  <td className="w-25 p-25-5">Seed · numbers from</td>
                  <td className="w-10 p-25-5"></td>
                </tr>
                {filtered.map((r) => {
                  const canView = r.entrantCount > 0 || r.draw;
                  return (
                    <tr
                      key={r.competition._id}
                      style={{ background: "#FFFFFF", fontSize: "12px", height: "31px" }}
                    >
                      <td className="w-10 grey-30 p-25-5">{r.competition.code}</td>
                      <td className="w-35 grey-30 p-25-5">{r.competition.name}</td>
                      <td className="text-center w-10 grey-30 p-25-5">{r.entrantCount}</td>
                      <td className="w-10 grey-30 p-25-5">{statusText(r)}</td>
                      <td className="w-25 grey-30 p-25-5">
                        {r.draw ? (
                          <>
                            {r.draw.seed}
                            {r.draw.numberBase ? ` · ${r.draw.numberBase}` : ""}
                          </>
                        ) : (
                          ""
                        )}
                      </td>
                      <td className="w-10 p-25-5">
                        <button
                          className={canView && !busy ? "text-btn" : "text-btn-disabled"}
                          onClick={() => canView && openComp(r.competition._id)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!filtered.length && (
                  <tr style={{ background: "#FFFFFF", fontSize: "12px" }}>
                    <td colSpan={6} className="text-center grey-30 p-25-5">
                      No competitions match
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && selected && (
        <div className="db-body">
          <div className="grey-42 mt-3" style={{ fontSize: "13px" }}>
            <b className="grey-30">
              {selectedRow()?.competition?.code} — {selectedRow()?.competition?.name}
            </b>{" "}
            · {selectedRow()?.entrantCount || 0}{" "}
            {selectedRow()?.entrantCount === 1 ? "entrant" : "entrants"}
          </div>

          {!locked && jobDevice && (
            <div className="grey-42 mt-4" style={{ fontSize: "13px" }}>
              Not locked yet.
            </div>
          )}
          {!locked && !jobDevice && (
            <div className="grey-42 mt-4" style={{ fontSize: "13px" }}>
              Not locked yet: draw and lock it on the{" "}
              <button className="text-btn" onClick={() => navigate(`/oireachtas/draws?id=${eventId}&comp=${selected}`)}>
                Draw screen
              </button>
              .
            </div>
          )}

          {(locked || draw) && (
            <div className="d-flex flex-wrap align-items-center gap-3 mt-4">
              {locked && (
                <>
                  {can("ox.print.cards") && (
                    <button
                      className={busy ? "text-btn-disabled" : "text-btn"}
                      onClick={() => dl("cards.pdf", "cards")}
                    >
                      Print number cards
                    </button>
                  )}
                  {can("ox.print.sheets") && (
                    <>
                      <button
                        className={busy ? "text-btn-disabled" : "text-btn"}
                        onClick={() => dl("checkin.pdf?round=1", "checkin")}
                      >
                        Print check-in sheet
                      </button>
                      <button
                        className={busy ? "text-btn-disabled" : "text-btn"}
                        onClick={() => dl("scoresheets.pdf", "scoresheets")}
                      >
                        Print score sheets
                      </button>
                    </>
                  )}
                </>
              )}
              <button
                className={busy ? "text-btn-disabled" : "text-btn"}
                onClick={doExportComp}
              >
                Export (Excel)
              </button>
            </div>
          )}

          {draw && (
            <div className="grey-42 mt-3" style={{ fontSize: "13px" }}>
              Seed <b className="grey-30">{draw.seed}</b> · Version{" "}
              <b className="grey-30">{draw.version}</b>
              {draw.numberBase ? (
                <>
                  {" "}
                  · Numbers from <b className="grey-30">{draw.numberBase}</b>
                </>
              ) : null}
              {draw.rounds > 1 && (
                <>
                  {" "}
                  · Round starts <b className="grey-30">{draw.roundStarts.join(", ")}</b>
                </>
              )}{" "}
              ·{" "}
              {locked ? (
                <b style={{ color: "#68AD3F" }}>Locked</b>
              ) : (
                <b style={{ color: "#FBB040" }}>Draft — not yet locked</b>
              )}
            </div>
          )}

          {!draw && !busy && (
            <div className="d-flex justify-content-center my-5">Not drawn yet — use the Draw screen</div>
          )}

          {draw && (
            <div style={{ width: "100%", overflowX: "auto", marginTop: "30px", marginBottom: "50px" }}>
              <table className="table my_table competitorTable ">
                <tbody>
                  <tr className="header-row">
                    <td className="text-center w-10 p-25-5">#</td>
                    <td className="mx-5 w-40 p-25-5">
                      {draw.entryType === "team" ? "Team" : "Dancer"}
                    </td>
                    <td className="w-40 p-25-5">School</td>
                  </tr>
                  {draw.order.map((o) => (
                    <tr key={o.ref} style={{ background: "#FFFFFF", fontSize: "12px", height: "31px" }}>
                      <td className="text-center w-10 grey-30 p-25-5">{o.number}</td>
                      <td className="mx-5 w-40 grey-30 p-25-5">{o.label || o.schoolName}</td>
                      <td className="w-40 grey-30 p-25-5">{o.schoolName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <ToastContainer />
      <Help />
    </>
  );
};

export default OireachtasDraw;
